<template>
    <el-card shadow="hover" class="duplicate-review-card">
        <div class="duplicate-review-card__header">
            <div>
                <div class="duplicate-review-card__title">复核记录 #{{ record.reviewRecordId || "-" }}</div>
                <div class="duplicate-review-card__meta">
                    <span>64位 hash：{{ sourceHashText }}</span>
                    <span>32位 md5：{{ md5HashText }}</span>
                    <span v-if="record.llmCategoryLevel1">一级分类：{{ record.llmCategoryLevel1 }}</span>
                    <el-tag size="mini" :type="recordStatusType">{{ record.recordStatus || "unknown" }}</el-tag>
                </div>
            </div>
            <el-tag size="mini" :type="reviewStatusType">{{ statusLabel }}</el-tag>
        </div>
        <div class="duplicate-review-card__body">
            <div class="duplicate-review-card__preview">
                <div class="duplicate-review-card__image-title">
                    {{ record.leftMaterialLabel || "候选图片" }}
                </div>
                <div v-if="showLeftLoading" class="duplicate-review-card__loading">
                    <i class="el-icon-loading duplicate-review-card__loading-icon" />
                    <span>图片加载中</span>
                </div>
                <img
                    v-else-if="showLeftImage"
                    ref="leftImage"
                    :src="leftDisplayUrl"
                    class="duplicate-review-card__image"
                    loading="lazy"
                    decoding="async"
                    fetchpriority="low"
                    @load="handleLeftImageLoad"
                    @error="handleLeftImageError"
                />
                <div v-else class="duplicate-review-card__placeholder">候选图片不可用</div>
            </div>
            <div class="duplicate-review-card__cases">
                <div class="duplicate-review-card__cases-header">
                    <div class="duplicate-review-card__cases-title">
                        <span>包含该图片的案件</span>
                        <el-tag size="mini" type="info">{{ record.duplicateCaseCount || 0 }}</el-tag>
                    </div>
                    <button
                        v-if="showCaseToggle"
                        type="button"
                        class="duplicate-review-card__toggle"
                        @click="$emit('toggle-case-list')"
                    >
                        {{ caseToggleLabel }}
                    </button>
                </div>
                <div v-if="showCaseSummary" class="duplicate-review-card__case-summary">
                    默认展示前 {{ previewCaseCount }} 个案件，点击后按页加载更多
                </div>
                <div v-if="caseState.loading" class="duplicate-review-card__case-loading">
                    <i class="el-icon-loading duplicate-review-card__loading-icon" />
                    <span>关联案件加载中</span>
                </div>
                <div v-if="caseState.error" class="duplicate-review-card__warning">{{ caseState.error }}</div>
                <div
                    v-if="duplicateCases.length"
                    class="duplicate-review-card__case-list"
                    :class="{ 'duplicate-review-card__case-list--collapsed': !caseState.expanded }"
                >
                    <button
                        v-for="item in duplicateCases"
                        :key="item.key || `${item.targetCaseNo}-${item.targetMaterialId || item.targetFileHash}`"
                        type="button"
                        class="duplicate-review-card__case-item"
                        @click="$emit('open-duplicate-case', item)"
                    >
                        <span class="duplicate-review-card__case-no">{{ item.targetCaseNo }}</span>
                        <span v-if="item.targetMaterialType" class="duplicate-review-card__case-type">{{ item.targetMaterialType }}</span>
                        <span v-if="item.matchCountInCase" class="duplicate-review-card__case-count">命中 {{ item.matchCountInCase }} 张</span>
                    </button>
                </div>
                <div v-else class="duplicate-review-card__case-empty">暂无关联案件</div>
                <div
                    v-if="showCasePagination"
                    class="duplicate-review-card__case-pagination"
                >
                    <el-pagination
                        small
                        background
                        layout="prev, pager, next"
                        :current-page="caseState.pageNo || 1"
                        :page-size="caseState.pageSize || defaultCasePageSize"
                        :total="caseState.total || 0"
                        @current-change="$emit('change-case-page', $event)"
                    />
                </div>
            </div>
            <div class="duplicate-review-card__side">
                <div class="duplicate-review-card__context">{{ record.contextSummary || "暂无上下文摘要" }}</div>
                <slot name="actions" />
                <div v-if="historyVisible" class="duplicate-review-card__history">
                    <div class="duplicate-review-card__history-title">历史变更记录和原因</div>
                    <el-timeline v-if="historyItems.length">
                        <el-timeline-item
                            v-for="item in historyItems"
                            :key="`${record.riskEventId || record.reviewRecordId}-${item.historyId}`"
                            :timestamp="item.changedAt"
                        >
                            <div class="duplicate-review-card__history-reason">
                                {{ item.reasonDesc || item.reasonCode || "无原因" }}
                            </div>
                            <div class="duplicate-review-card__history-detail">
                                {{ buildHistorySummary(item) }}
                            </div>
                        </el-timeline-item>
                    </el-timeline>
                    <el-empty
                        v-else-if="!historyLoading"
                        :image-size="60"
                        description="暂无历史"
                    />
                </div>
            </div>
        </div>
    </el-card>
</template>

<script>
export default {
    name: "DuplicateReviewCard",
    props: {
        record: {
            type: Object,
            default() {
                return {};
            },
        },
        duplicateCases: {
            type: Array,
            default() {
                return [];
            },
        },
        caseState: {
            type: Object,
            default() {
                return {};
            },
        },
        historyVisible: {
            type: Boolean,
            default: false,
        },
        historyLoading: {
            type: Boolean,
            default: false,
        },
        historyItems: {
            type: Array,
            default() {
                return [];
            },
        },
    },
    data() {
        return {
            leftImageFailed: false,
            leftImageLoaded: false,
            leftDisplayUrl: "",
        };
    },
    created() {
        this.refreshImageUrls();
    },
    mounted() {
        this.syncLeftImageLoadedState();
    },
    computed: {
        previewCaseCount() {
            return 12;
        },
        defaultCasePageSize() {
            return 20;
        },
        reviewStatusType() {
            switch (this.record.reviewStatus) {
                case "useful":
                    return "success";
                case "not-useful":
                    return "info";
                default:
                    return "warning";
            }
        },
        recordStatusType() {
            switch (this.record.recordStatus) {
                case "ready":
                    return "success";
                case "partial":
                    return "warning";
                default:
                    return "danger";
            }
        },
        statusLabel() {
            switch (this.record.reviewStatus) {
                case "useful":
                    return "有用";
                case "not-useful":
                    return "无用";
                default:
                    return "待定";
            }
        },
        sourceHashText() {
            const candidateValues = [
                this.record.sourceHash,
                this.record.sourceCaseNo,
            ];
            const matched = candidateValues.find((item) => /^[a-fA-F0-9]{64}$/.test(String(item || "").trim()));
            return matched || "-";
        },
        md5HashText() {
            const candidateValues = [
                this.record.md5Hash,
                this.record.targetFileHash,
                this.record.targetCaseNo,
            ];
            const matched = candidateValues.find((item) => /^[a-fA-F0-9]{32}$/.test(String(item || "").trim()));
            return matched || "-";
        },
        hasLeftImage() {
            return Boolean(this.record.leftImageUrl);
        },
        showLeftLoading() {
            return this.hasLeftImage && !this.leftDisplayUrl && !this.leftImageFailed;
        },
        showLeftImage() {
            return Boolean(this.leftDisplayUrl) && !this.leftImageFailed;
        },
        hiddenCaseCount() {
            const total = Number(this.caseState.total || this.record.duplicateCaseCount || 0) || 0;
            return Math.max(total - this.previewCaseCount, 0);
        },
        showCaseToggle() {
            const total = Number(this.caseState.total || this.record.duplicateCaseCount || 0) || 0;
            return total > this.previewCaseCount || this.caseState.expanded;
        },
        showCaseSummary() {
            return this.showCaseToggle && !this.caseState.expanded;
        },
        caseToggleLabel() {
            if (this.caseState.expanded) {
                return "收起";
            }
            const total = Number(this.caseState.total || this.record.duplicateCaseCount || 0) || 0;
            return total > this.previewCaseCount ? `查看更多案件（共 ${total} 个）` : "查看案件";
        },
        showCasePagination() {
            if (!this.caseState.expanded) {
                return false;
            }
            const total = Number(this.caseState.total || 0) || 0;
            const pageSize = Number(this.caseState.pageSize || this.defaultCasePageSize) || this.defaultCasePageSize;
            return total > pageSize;
        },
    },
    watch: {
        "record.leftImageUrl"() {
            this.leftImageFailed = false;
            this.leftImageLoaded = false;
            this.refreshImageUrls();
        },
        "record.reviewRecordId"() {
            this.leftImageFailed = false;
            this.leftImageLoaded = false;
            this.refreshImageUrls();
        },
        leftDisplayUrl() {
            this.$nextTick(() => {
                this.syncLeftImageLoadedState();
            });
        },
    },
    methods: {
        refreshImageUrls() {
            this.leftDisplayUrl = String(this.record?.leftImageUrl || "").trim();
        },
        syncLeftImageLoadedState() {
            const img = this.$refs.leftImage;
            if (!img || !this.leftDisplayUrl) {
                return;
            }
            if (img.complete && img.naturalWidth > 0) {
                this.leftImageLoaded = true;
            }
        },
        handleLeftImageLoad() {
            this.leftImageLoaded = true;
        },
        handleLeftImageError() {
            this.leftImageFailed = true;
        },
        resolveStatusLabel(value) {
            const labelMap = {
                pending: "待定",
                useful: "有用",
                "not-useful": "无用",
            };
            return labelMap[value] || (value === null || value === undefined || value === "" ? "-" : String(value));
        },
        buildHistorySummary(item = {}) {
            const beforeDecision = this.resolveStatusLabel(item.beforeReviewDecision || item.beforeReviewStatus);
            const afterDecision = this.resolveStatusLabel(item.afterReviewDecision || item.afterReviewStatus);
            const beforeStatus = item.beforeRecordStatus || "-";
            const afterStatus = item.afterRecordStatus || "-";
            return `核验结果 ${beforeDecision} → ${afterDecision}；记录状态 ${beforeStatus} → ${afterStatus}`;
        },
    },
};
</script>

<style scoped>
.duplicate-review-card {
    margin-bottom: 12px;
}

.duplicate-review-card__header,
.duplicate-review-card__meta,
.duplicate-review-card__body {
    display: flex;
}

.duplicate-review-card__header {
    justify-content: space-between;
    gap: 10px;
}

.duplicate-review-card__title {
    font-weight: 600;
    color: #1f2d3d;
    line-height: 1.2;
}

.duplicate-review-card__meta {
    gap: 6px;
    margin-top: 4px;
    color: #606266;
    flex-wrap: wrap;
    font-size: 12px;
}

.duplicate-review-card__body {
    gap: 10px;
    margin-top: 12px;
    align-items: flex-start;
}

.duplicate-review-card__preview {
    width: 120px;
    flex: 0 0 120px;
}

.duplicate-review-card__side {
    width: 200px;
    flex: 0 0 200px;
    display: flex;
    flex-direction: column;
    align-self: stretch;
    gap: 8px;
}

.duplicate-review-card__image-title {
    margin-bottom: 6px;
    color: #606266;
    font-size: 12px;
    line-height: 1.2;
}

.duplicate-review-card__image,
.duplicate-review-card__placeholder {
    width: 100%;
    height: 92px;
    border-radius: 6px;
    border: 1px solid #e4e7ed;
    object-fit: contain;
    background: #f5f7fa;
}

.duplicate-review-card__placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
}

.duplicate-review-card__loading {
    width: 100%;
    height: 92px;
    border-radius: 6px;
    border: 1px solid #e4e7ed;
    background: linear-gradient(180deg, #fbfdff 0%, #f5f7fa 100%);
    color: #909399;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.duplicate-review-card__loading-icon {
    font-size: 16px;
}

.duplicate-review-card__context {
    color: #303133;
    line-height: 1.4;
    font-size: 13px;
}

.duplicate-review-card__history {
    margin-top: 8px;
    padding-top: 12px;
    border-top: 1px dashed #dcdfe6;
    background: #fafbfc;
    border-radius: 8px;
    padding-left: 10px;
    padding-right: 10px;
    padding-bottom: 6px;
}

.duplicate-review-card__history-title {
    margin: 0 0 8px;
    color: #606266;
    font-size: 12px;
    font-weight: 600;
}

.duplicate-review-card__history-reason {
    color: #1f2d3d;
    font-weight: 500;
    line-height: 1.5;
}

.duplicate-review-card__history-detail {
    margin-top: 4px;
    color: #5e6b80;
    line-height: 1.5;
    font-size: 12px;
}

.duplicate-review-card__warning {
    margin-top: 6px;
    color: #e6a23c;
    font-size: 12px;
}

.duplicate-review-card__cases {
    flex: 1;
    min-width: 0;
    padding: 2px 0;
}

.duplicate-review-card__cases-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
}

.duplicate-review-card__cases-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #1f2d3d;
    font-weight: 600;
}

.duplicate-review-card__toggle {
    border: none;
    background: transparent;
    color: #409eff;
    cursor: pointer;
    padding: 0;
    font-size: 12px;
}

.duplicate-review-card__toggle:hover {
    color: #66b1ff;
}

.duplicate-review-card__case-summary {
    margin-top: 6px;
    color: #909399;
    font-size: 12px;
}

.duplicate-review-card__case-loading {
    margin-top: 8px;
    color: #909399;
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.duplicate-review-card__case-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
    align-content: flex-start;
    overflow: hidden;
}

.duplicate-review-card__case-list--collapsed {
    max-height: 58px;
}

.duplicate-review-card__case-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #dcdfe6;
    background: #fff;
    border-radius: 999px;
    padding: 4px 10px;
    cursor: pointer;
    color: #303133;
    font-size: 12px;
}

.duplicate-review-card__case-item:hover {
    border-color: #409eff;
    color: #409eff;
}

.duplicate-review-card__case-pagination {
    margin-top: 10px;
    display: flex;
    justify-content: center;
}

.duplicate-review-card__case-no {
    font-weight: 600;
}

.duplicate-review-card__case-type,
.duplicate-review-card__case-count,
.duplicate-review-card__case-empty {
    color: #606266;
}

.duplicate-review-card__case-empty {
    margin-top: 8px;
    font-size: 12px;
}

@media (max-width: 768px) {
    .duplicate-review-card__body {
        flex-direction: column;
    }

    .duplicate-review-card__preview,
    .duplicate-review-card__side {
        width: 100%;
        flex: auto;
    }
}
</style>
